import { Button, Dialog, Flex, Link, Text } from '@buffer-mono/popcorn'
import { useMemo } from 'react'
import { unparse } from 'papaparse'
import { Link as RouterLink } from 'react-router-dom-v5-compat'
import { channel } from '~publish/legacy/routes'
import { ConfirmationDialog } from '../ConfirmationDialog'
import type { ListItem } from './PostItems'
import styles from './PostItems.module.css'

const isError = (item: ListItem): boolean => item.status === 'error'
const isSuccess = (item: ListItem): boolean => item.status === 'success'
const isValid = (item: ListItem): boolean => item.status === 'valid'

export const PostItemsFooter = ({
  channelId,
  channelName,
  items,
  selectedSaveAction,
  isCreatingPosts,
  canStartPostCreation,
  isPostCreationComplete,
  onCreatePosts,
  onDialogClose,
}: {
  channelId: string
  channelName: string
  items: ListItem[]
  selectedSaveAction: 'post' | 'draft'
  isCreatingPosts: boolean
  canStartPostCreation: boolean
  isPostCreationComplete: boolean
  onCreatePosts: (type: 'post' | 'draft') => void
  onDialogClose: (closeAction: 'success' | 'cancel') => void
}): JSX.Element => {
  const errorItemCount = useMemo(() => items.filter(isError).length, [items])
  const successItemCount = useMemo(
    () => items.filter(isSuccess).length,
    [items],
  )
  const validItemCount = useMemo(() => items.filter(isValid).length, [items])

  const postCountFooterText = useMemo(() => {
    if (isPostCreationComplete || isCreatingPosts) {
      if (selectedSaveAction === 'draft') {
        return `${successItemCount} draft${
          successItemCount > 1 ? 's' : ''
        } saved${errorItemCount > 0 ? `, ${errorItemCount} failed` : ''}`
      }
      return `${successItemCount} post${
        successItemCount > 1 ? 's' : ''
      } scheduled${errorItemCount > 0 ? `, ${errorItemCount} failed` : ''}`
    }

    return `${items.length} post${items.length > 1 ? 's' : ''} imported`
  }, [
    isPostCreationComplete,
    isCreatingPosts,
    successItemCount,
    items,
    errorItemCount,
    selectedSaveAction,
  ])

  // Construct a CSV of failed posts using papaparse with the following columns: Text, Image URL, Tags, Errors
  const shouldShowFailedPostsCsv = useMemo(() => {
    const hasErrors = errorItemCount > 0
    return hasErrors && isPostCreationComplete
  }, [errorItemCount, isPostCreationComplete])

  const failedPostsCsv = useMemo(() => {
    const failedItems = items.filter(isError)
    if (failedItems.length === 0 || !isPostCreationComplete) return ''

    const data = failedItems.map((item) => ({
      Text: item.data.text,
      'Image URL': item.data.media?.photo || '',
      Tags: item.data.tags.map((tag) => tag.name).join(','),
      Errors: item.errors?.join(',') || '',
    }))

    return unparse(data, {
      quotes: true,
    })
  }, [items, isPostCreationComplete])

  const onDownloadFailedPostsCsv = useMemo(
    () => (): void => {
      const blob = new Blob([failedPostsCsv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'failed-posts.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
    [failedPostsCsv],
  )

  return (
    <Dialog.Footer className={styles.footer}>
      <Flex>
        <Text>
          {postCountFooterText}{' '}
          {shouldShowFailedPostsCsv && (
            <Link as="button" onClick={onDownloadFailedPostsCsv}>
              Download CSV with post errors
            </Link>
          )}
        </Text>
      </Flex>
      {canStartPostCreation || isCreatingPosts ? (
        <Flex gap="sm">
          <ConfirmationDialog
            count={validItemCount}
            channelName={channelName}
            postType="draft"
            onConfirm={() => onCreatePosts('draft')}
          >
            <Button size="large" variant="secondary" loading={isCreatingPosts}>
              {isCreatingPosts ? 'Processing...' : 'Save as Drafts'}
            </Button>
          </ConfirmationDialog>
          <ConfirmationDialog
            count={validItemCount}
            channelName={channelName}
            postType="post"
            onConfirm={() => onCreatePosts('post')}
          >
            <Button size="large" loading={isCreatingPosts}>
              {isCreatingPosts ? 'Processing...' : 'Add Posts to Queue'}
            </Button>
          </ConfirmationDialog>
        </Flex>
      ) : isPostCreationComplete ? (
        <Flex gap="sm">
          <Dialog.Close onClick={() => onDialogClose('success')}>
            <Button
              variant={successItemCount > 0 ? 'tertiary' : 'secondary'}
              size="large"
            >
              Done
            </Button>
          </Dialog.Close>
          {successItemCount > 0 && (
            <Button
              as={RouterLink}
              to={channel.getRoute(channelId, {
                tab: selectedSaveAction === 'draft' ? 'drafts' : 'queue',
              })}
              onClick={() => onDialogClose('success')}
              variant="secondary"
              size="large"
            >
              {selectedSaveAction === 'draft' ? 'View Drafts' : 'View Posts'}
            </Button>
          )}
        </Flex>
      ) : (
        <Dialog.Close onClick={() => onDialogClose('cancel')}>
          <Button variant="secondary" size="large">
            Done
          </Button>
        </Dialog.Close>
      )}
    </Dialog.Footer>
  )
}
